import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, BarChart3, CreditCard, Stethoscope, 
  UserCog, ShoppingBag, FileText, Gift, Award, Tag, 
  Zap, MessageSquare, Menu, X, ChevronDown, ChevronRight, 
  HelpCircle, Upload, Download, RotateCcw, Palette, Bot, Mail
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    reports: true,
    manage: true,
    appbuilder: true
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useBranding();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Restructured navigation - cleaned up per user requirements
  const navSections = [
    {
      id: 'overview',
      title: 'OVERVIEW',
      items: [
        { name: 'Platform Overview', href: '/admin', icon: LayoutDashboard },
        { name: 'Client Profiles', href: '/admin/patients', icon: Users, hasExport: true },
      ]
    },
    {
      id: 'reports',
      title: 'REPORTS',
      items: [
        { name: 'Shop Summary', href: '/admin/reports', icon: BarChart3, hasExport: true },
        { name: 'Memberships', href: '/admin/reports/memberships', icon: CreditCard, hasExport: true },
        { name: 'Services', href: '/admin/reports/services', icon: Stethoscope, hasExport: true },
        { name: 'Redemptions', href: '/admin/redemptions', icon: RotateCcw, hasExport: true },
      ]
    },
    {
      id: 'manage',
      title: 'MANAGE',
      items: [
        { name: 'Staff Directory', href: '/admin/staff', icon: UserCog },
        { name: 'Products', href: '/admin/products', icon: ShoppingBag },
        { name: 'Custom Plans', href: '/admin/packages', icon: FileText },
        { name: 'Gift Cards', href: '/admin/giftcards', icon: Gift },
        { name: 'Rewards', href: '/admin/rewards', icon: Award },
        { name: 'Special Offers', href: '/admin/special-offers', icon: Tag },
        { name: 'Automated Offers', href: '/admin/automated-offers', icon: Zap },
        { name: 'AI Advisors', href: '/admin/ai-advisors', icon: Bot },
        { name: 'Text Messaging', href: '/admin/sms', icon: MessageSquare },
        { name: 'Email Templates', href: '/admin/email-templates-editor', icon: Mail },
        { name: 'Data Import', href: '/admin/import', icon: Upload },
      ]
    },
    {
      id: 'appbuilder',
      title: 'APP BUILDER',
      items: [
        { name: 'Builder & Settings', href: '/admin/app-builder', icon: Palette },
      ]
    }
  ];

  const isItemActive = (href) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <Link to="/admin" className="flex items-center space-x-2">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={branding.practiceName} className="h-8 w-auto" />
              ) : (
                <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                  <span className="text-rose-600 font-bold text-sm">M</span>
                </div>
              )}
              <span className="text-lg font-semibold text-gray-900">MdSpa360</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navSections.map((section) => (
              <div key={section.id} className="mb-4">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
                >
                  {section.title}
                  {expandedSections[section.id] ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>

                {/* Section Items */}
                {expandedSections[section.id] && (
                  <div className="mt-1 space-y-1">
                    {section.items.map((item) => {
                      const isActive = isItemActive(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            isActive
                              ? 'bg-rose-50 text-rose-600'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <div className="flex items-center">
                            <item.icon className={`h-4 w-4 mr-3 ${isActive ? 'text-rose-600' : 'text-gray-400'}`} />
                            {item.name}
                          </div>
                          {item.hasExport && (
                            <Download className="h-3 w-3 text-gray-400" />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {/* Help at bottom */}
            <div className="pt-4 border-t border-gray-200 mt-4">
              <Link
                to="/admin/help"
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === '/admin/help'
                    ? 'bg-rose-50 text-rose-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <HelpCircle className="h-4 w-4 mr-3 text-gray-400" />
                Help Center
              </Link>
            </div>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-rose-600 font-semibold">
                  {user?.name?.split(' ').map(n => n[0]).join('') || 'AD'}
                </span>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin User'}</p>
                <p className="text-xs text-gray-500">{user?.email || 'admin@practice.com'}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-900"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex-1" />
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm" asChild>
                <Link to="/">View Site</Link>
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
